import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BaseAppService from '../../../../application/platform/scp/base/base-app-service';
import PurchaseAppService from '../../../../application/platform/scp/purchase/purchase-app-service';
import { ACTION_TYPES, usePurchaseSearchDispatch } from './purchase-search-context';
import { useFeedback } from '../../../../hooks/use-feedback';
import { useSearchActions } from '../../../../hooks/use-search-actions';
import { IPurchasePageRequest } from '../../../../model/scp/purchase/request/purchase-paged-request';
import { parseNumberInput } from '../../../../utils/format';
import { parseSort } from '../../shared/scp-utils';
import { PurchaseSortField } from '../../../../model/scp/purchase/purchase-sort-field';
import { SortDirection } from '../../../../model/scp/base/sort-direction';
import { IPurchasePagedResponse } from '../../../../model/scp/purchase/response/purchase-paged-response';

const DEFAULT_SORT = { sortBy: PurchaseSortField.UpdatedDate, sortDirection: SortDirection.Descending };

type SearchParameters = IPurchasePageRequest & { page: number; pageSize: number };

const EMPTY_FILTERS: Partial<SearchParameters> = { ...DEFAULT_SORT, term: undefined, clientId: undefined, modalityId: undefined, situationId: undefined, dispupteModeId: undefined, year: undefined };

export const usePurchaseSearchController = () => {
    const navigate = useNavigate();
    const { showError, showSuccess, confirmDeleteWithJustification, withLoading } = useFeedback();
    const dispatch = usePurchaseSearchDispatch();
    const { updateParameters, handleSearch, handleClearFilters, handlePaginationChange } = useSearchActions<SearchParameters>(dispatch, ACTION_TYPES, EMPTY_FILTERS);

    useEffect(() => {
        Promise.all([BaseAppService.listDisputeMode(), BaseAppService.listSituationPurchase(), BaseAppService.listModality()])
            .then(([disputeMode, situation, modality]) => {
                dispatch({ type: ACTION_TYPES.setDisputeMode, payload: disputeMode });
                dispatch({ type: ACTION_TYPES.setSituation, payload: situation });
                dispatch({ type: ACTION_TYPES.setModality, payload: modality });
            })
            .catch((error: any) => showError('Não foi possível carregar os filtros de pesquisa', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleOnChange(e: any) {
        const { name, value } = e.target;
        switch (name) {
            case 'input-term':
                return updateParameters({ term: value || undefined });
            case 'select-situation':
                return updateParameters({ situationId: parseNumberInput(value) });
            case 'select-dispute-mode':
                return updateParameters({ dispupteModeId: parseNumberInput(value) });
            case 'select-modality':
                return updateParameters({ modalityId: parseNumberInput(value) });
            case 'select-client':
                return updateParameters({ clientId: parseNumberInput(value) });
            case 'input-year':
                return updateParameters({ year: parseNumberInput(value) });
            case 'select-sort':
                return updateParameters(parseSort(value) ?? DEFAULT_SORT);
        }
    }

    function navigateToCreate() {
        navigate('/compras-licitacoes/publicar');
    }

    function navigateToEdit(row: IPurchasePagedResponse) {
        navigate(`/compras-licitacoes/publicar/${row.id}`);
    }

    function handleDelete(row: IPurchasePagedResponse, onDeleted: () => void) {
        confirmDeleteWithJustification(
            `Tem certeza que deseja excluir a compra/licitação "${row.number}"?`,
            'Informe a justificativa para excluir a compra/licitação',
            async (justification) => {
                try {
                    await withLoading(() => PurchaseAppService.deletePurchase(justification, row.id));
                    showSuccess('Compra/licitação excluída com sucesso!');
                    onDeleted();
                } catch (error: any) {
                    showError('Não foi possível excluir a compra/licitação', error);
                }
            }
        );
    }

    return {
        handleOnChange,
        handleSearch,
        handleClearFilters,
        handlePaginationChange,
        navigateToCreate,
        navigateToEdit,
        handleDelete,
    };
};
